// Live valuation of wealth holdings.
//
// Holding kinds:
//   cash   – a fixed £ amount you edit yourself (cash, LISA cash, untracked funds)
//   crypto – quantity of a coin, priced from CoinGecko
//   metal  – grams/ounces of gold or silver, priced from CoinGecko (PAXG / KAG,
//            tokens that track one troy ounce of gold / silver)
//   stock  – units of a share, ETF or fund, priced via /api/quote (Yahoo)
//
// Prices are cached inside the encrypted vault, so offline you see the last
// known values along with when they were fetched.
import { getState, update } from "./vault";

export const TROY_OUNCE_GRAMS = 31.1034768;
const METAL_COINS = { gold: "pax-gold", silver: "kinesis-silver" };
const COINGECKO = "https://api.coingecko.com/api/v3";

export const HOLDING_KINDS = {
  cash: "Cash / manual",
  crypto: "Crypto",
  metal: "Gold & silver",
  stock: "Shares & funds",
};

export function priceKey(h) {
  if (h.kind === "crypto") return `crypto:${h.coinId}`;
  if (h.kind === "metal") return `crypto:${METAL_COINS[h.metal]}`;
  if (h.kind === "stock") return `stock:${h.symbol}`;
  return null;
}

// -> { value: number|null, unitPrice: number|null }
export function holdingValue(h, quotes) {
  if (h.kind === "cash") return { value: Number(h.amount) || 0, unitPrice: null };
  const q = quotes[priceKey(h)];
  if (!q || q.gbp == null) return { value: null, unitPrice: null };
  const qty = Number(h.quantity) || 0;
  if (h.kind === "metal") {
    const perUnit = h.unit === "g" ? q.gbp / TROY_OUNCE_GRAMS : q.gbp;
    return { value: qty * perUnit, unitPrice: perUnit };
  }
  return { value: qty * q.gbp, unitPrice: q.gbp };
}

export function accountValue(account, quotes) {
  let value = 0;
  let missing = 0;
  for (const h of account.holdings) {
    const v = holdingValue(h, quotes).value;
    if (v == null) missing += 1;
    else value += v;
  }
  return { value, missing };
}

export const portfolioValue = (accounts, quotes) =>
  accounts.reduce((sum, a) => sum + accountValue(a, quotes).value, 0);

async function fetchJson(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

async function fetchCrypto(ids) {
  if (!ids.length) return {};
  const data = await fetchJson(
    `${COINGECKO}/simple/price?ids=${ids.map(encodeURIComponent).join(",")}&vs_currencies=gbp`
  );
  const now = Date.now();
  const out = {};
  for (const id of ids) {
    if (data[id]?.gbp != null) out[`crypto:${id}`] = { gbp: data[id].gbp, at: now };
  }
  return out;
}

async function fetchStocks(symbols) {
  if (!symbols.length) return {};
  const data = await fetchJson(`/api/quote?symbols=${symbols.map(encodeURIComponent).join(",")}`);
  const now = Date.now();
  const out = {};
  for (const s of symbols) {
    const q = data[s];
    if (q?.gbp != null) out[`stock:${s}`] = { gbp: q.gbp, at: now, name: q.name };
  }
  return out;
}

let inflight = null;

// Fetch fresh prices for everything held. Keeps old prices for anything that
// fails. Returns { ok, failed } where failed lists the sources that errored.
export function refreshPrices() {
  if (inflight) return inflight;
  inflight = (async () => {
    const { accounts } = getState();
    const holdings = accounts.flatMap((a) => a.holdings);
    const coinIds = new Set();
    const symbols = new Set();
    for (const h of holdings) {
      if (h.kind === "crypto" && h.coinId) coinIds.add(h.coinId);
      if (h.kind === "metal") coinIds.add(METAL_COINS[h.metal]);
      if (h.kind === "stock" && h.symbol) symbols.add(h.symbol);
    }

    const failed = [];
    const results = await Promise.all([
      fetchCrypto([...coinIds]).catch(() => (failed.push("crypto & metals"), {})),
      fetchStocks([...symbols]).catch(() => (failed.push("shares & funds"), {})),
    ]);
    const fresh = Object.assign({}, ...results);

    await update((s) => {
      s.prices.quotes = { ...s.prices.quotes, ...fresh };
      if (Object.keys(fresh).length) s.prices.fetchedAt = Date.now();
    });
    return { ok: failed.length === 0, failed };
  })().finally(() => {
    inflight = null;
  });
  return inflight;
}

// Fetch one price straight away (used when adding a holding).
export async function fetchPriceFor(h) {
  const key = priceKey(h);
  if (!key) return null;
  const fresh =
    h.kind === "stock"
      ? await fetchStocks([h.symbol])
      : await fetchCrypto([h.kind === "metal" ? METAL_COINS[h.metal] : h.coinId]);
  if (fresh[key]) {
    await update((s) => {
      s.prices.quotes[key] = fresh[key];
    });
  }
  return fresh[key] || null;
}

export async function searchCoins(query) {
  const q = query.trim();
  if (!q) return [];
  const data = await fetchJson(`${COINGECKO}/search?query=${encodeURIComponent(q)}`);
  return (data.coins || []).slice(0, 8).map((c) => ({
    id: c.id,
    name: c.name,
    symbol: c.symbol,
    thumb: c.thumb,
  }));
}

export async function searchStocks(query) {
  const q = query.trim();
  if (!q) return [];
  return fetchJson(`/api/search?q=${encodeURIComponent(q)}`);
}

export function holdingDescription(h) {
  const qty = Number(h.quantity) || 0;
  const n = (v) => v.toLocaleString("en-GB", { maximumFractionDigits: 8 });
  if (h.kind === "crypto") return `${n(qty)} ${String(h.coinSymbol || "").toUpperCase()}`;
  if (h.kind === "metal") return `${n(qty)} ${h.unit === "g" ? "g" : "oz"} ${h.metal}`;
  if (h.kind === "stock") return `${n(qty)} × ${h.symbol}`;
  return "Manual value";
}
